/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import type { Ref } from 'vue'

import { prefersReducedMotion } from '~/scopes/campaign/utils/prefersReducedMotion'

// Manual FLIP for cinema mode — no GSAP. We record the inline rect, let the frame
// take its cinema layout, then transform it back onto the inline rect and release
// the transition, so the frame appears to grow from where it sat (and shrink back
// onto it on exit). Both boxes are 16:9, so a single uniform scale is exact.
// Easing/duration come from the `--raw-out-quart--slow` token to match the house feel.

const FLIP_TRANSITION =
  'transform var(--raw-out-quart--slow), border-radius var(--raw-out-quart--slow)'
// A touch over the 600ms token, so a dropped `transitionend` still resolves.
const FALLBACK_MS = 700

// The corner radius the frame shows at each end, in px (root font-size is 16px).
// Because the transform is a uniform scale, a fixed radius would *scale with it* —
// so at grow-start the inline-sized frame would show `RADIUS_CINEMA * scale`, not
// the inline box's radius, and it would snap. We instead counter-scale: set the local
// radius to `R / scale` so the *visual* radius lands exactly on the target at each
// end (`RADIUS_INLINE` = the slot's `rounded-md`, `RADIUS_CINEMA` = the frame's
// `rounded-[1.5rem]`) and interpolates smoothly between, riding the same easing.
const RADIUS_INLINE = 8
const RADIUS_CINEMA = 24

// Uniform scale to make a box laid out at `to` look the size of `from` (both 16:9).
function scaleBetween(from: DOMRect, to: DOMRect): number {
  return to.width ? from.width / to.width : 1
}

// Transform that makes an element currently laid out at `to` look like it sits at
// `from` — top-left anchored so the translate + uniform scale compose cleanly.
function transformBetween(from: DOMRect, to: DOMRect): string {
  const dx = from.left - to.left
  const dy = from.top - to.top
  return `translate(${dx}px, ${dy}px) scale(${scaleBetween(from, to)})`
}

export function useCinemaFlip(frame: Ref<HTMLElement | null>) {
  let first: DOMRect | null = null
  // Detaches the listener + timer of any in-flight animation, so a stale
  // `done` can't fire after it's been interrupted (e.g. exit during the grow).
  let cancel: (() => void) | null = null

  function onceSettled(el: HTMLElement, cb: () => void) {
    cancel?.()
    const finish = () => {
      cancel = null
      el.removeEventListener('transitionend', onEnd)
      clearTimeout(timer)
      cb()
    }
    const onEnd = (event: TransitionEvent) => {
      if (event.target === el && event.propertyName === 'transform') finish()
    }
    el.addEventListener('transitionend', onEnd)
    const timer = setTimeout(finish, FALLBACK_MS)
    cancel = () => {
      el.removeEventListener('transitionend', onEnd)
      clearTimeout(timer)
      cancel = null
    }
  }

  // The element's untransformed layout rect: clear any transform, measure, and
  // pin it back to where it looked — all in one frame, so nothing paints between.
  function pinToCurrent(el: HTMLElement): DOMRect {
    const visual = el.getBoundingClientRect()
    el.style.transition = 'none'
    el.style.transform = ''
    const layout = el.getBoundingClientRect()
    el.style.transformOrigin = 'top left'
    el.style.transform = transformBetween(visual, layout)
    void el.offsetWidth
    return layout
  }

  // Call synchronously on the click, before the layout flips to cinema.
  function record() {
    first = frame.value?.getBoundingClientRect() ?? null
  }

  // Enter: grow from the recorded inline rect to the cinema layout. `done` runs
  // once the frame has settled. With no recorded rect (cold deep-link) or reduced
  // motion, it resolves immediately — a plain fade.
  function grow(done?: () => void) {
    const el = frame.value
    const from = first
    first = null
    cancel?.()
    if (!el || !from || prefersReducedMotion()) {
      done?.()
      return
    }
    el.style.transformOrigin = 'top left'
    el.style.transition = 'none'
    el.style.transform = ''
    const layout = el.getBoundingClientRect()
    const scale = scaleBetween(from, layout)
    el.style.transform = transformBetween(from, layout) // look inline
    el.style.borderRadius = `${RADIUS_INLINE / scale}px` // → 8px once scaled down
    void el.offsetWidth // reflow so the inverse becomes the paint baseline
    el.style.transition = FLIP_TRANSITION
    el.style.transform = '' // release → grows to the cinema layout
    el.style.borderRadius = `${RADIUS_CINEMA}px` // → 24px at identity
    onceSettled(el, () => {
      el.style.transition = ''
      el.style.transformOrigin = ''
      el.style.borderRadius = '' // hand the corners back to the class
      done?.()
    })
  }

  // Exit: shrink from wherever the frame currently is onto `target` (the inline
  // placeholder). It stays in cinema layout during the animation, so we transition
  // *to* a transform and leave it applied — the caller drops `?cinema` in `done`,
  // then calls `clear()` on the next tick once the Teleport has moved the frame
  // back inline (where the transform already has it visually). Interrupt-safe: it
  // pins to the current visual rect first, so closing mid-grow doesn't jump.
  function shrink(target: DOMRect | null, done?: () => void) {
    const el = frame.value
    cancel?.()
    if (!el || !target || prefersReducedMotion()) {
      done?.()
      return
    }
    const layout = pinToCurrent(el)
    const scale = scaleBetween(target, layout)
    // Pin the corners to an explicit 24px start (they came from the class), so the
    // radius has a value to transition *from* under `transition: none`.
    el.style.borderRadius = `${RADIUS_CINEMA}px`
    void el.offsetWidth
    el.style.transition = FLIP_TRANSITION
    el.style.transform = transformBetween(target, layout)
    el.style.borderRadius = `${RADIUS_INLINE / scale}px` // → 8px once scaled down
    onceSettled(el, () => done?.())
  }

  // Reset the frame's inline styles with no motion — call once it is back in its
  // inline layout so clearing the leftover transform doesn't animate.
  function clear() {
    const el = frame.value
    cancel?.()
    if (!el) return
    el.style.transition = 'none'
    el.style.transform = ''
    el.style.transformOrigin = ''
    el.style.borderRadius = '' // drop the leftover shrink radius; class/slot clip takes over
    void el.offsetWidth
    el.style.transition = ''
  }

  return { record, grow, shrink, clear }
}
